// src/modules/kinema/panels/FontPicker.tsx
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { FontFamily } from '@services/font/commands/cmdGetFontFamilies';
import { fetchFontFamilies, loadFont } from '../fonts';
import { useAssetKit, type AssetKitFont, type AssetKitTypographyStyle } from '../assetKit';

interface FontPickerProps {
  value: string;
  onChange: (family: string, variants: FontFamily) => void;
  /** Listed under "Brand Fonts". Defaults to the AssetKit's, so it works outside Create too; [] lists only Google Fonts. */
  brandFonts?: AssetKitFont[];
}

function assetKitFontsToFamilies(fonts: AssetKitFont[]): FontFamily[] {
  const map = new Map<string, FontFamily>();
  for (const f of fonts) {
    const existing = map.get(f.family);
    const variant = {
      id: 0,
      weight: f.weight ?? 400,
      italic: f.italic ?? false,
      format: 'woff2',
      variable: false,
      axes_json: null as any,
      file_url: f.url,
    };
    if (existing) {
      existing.variants.push(variant);
    } else {
      map.set(f.family, { family_name: f.family, source: 'brand', variants: [variant] });
    }
  }
  return Array.from(map.values());
}

/**
 * The brand's fonts, then each Google family its typography styles name (MPN-2033), once. A
 * Google family takes its variants from the catalog once that has loaded.
 */
function brandFontFamilies(fonts: AssetKitFont[], styles: AssetKitTypographyStyle[], googleFamilies: FontFamily[]): FontFamily[] {
  const families = fonts.length ? assetKitFontsToFamilies(fonts) : [];
  for (const style of styles) {
    if (style.source !== 'google') continue;
    if (families.some((f) => f.family_name.toLowerCase() === style.family.toLowerCase())) continue;
    families.push(googleFamilies.find((f) => f.family_name === style.family) ?? { family_name: style.family, source: 'google', variants: [] });
  }
  return families;
}

export function FontPicker({ value, onChange, brandFonts: brandFontsProp }: FontPickerProps) {
  const kit = useAssetKit();
  const brandFonts = brandFontsProp ?? kit?.fonts ?? [];
  // Given brand fonts, the picker is outside Create and lists only those.
  const brandStyles = brandFontsProp ? undefined : kit?.typography;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [googleFamilies, setGoogleFamilies] = useState<FontFamily[]>([]);
  const [loading, setLoading] = useState(false);

  const brandFamilies = useMemo(
    () => brandFontFamilies(brandFonts, brandStyles ?? [], googleFamilies),
    [brandFonts, brandStyles, googleFamilies],
  );
  const [loadingFont, setLoadingFont] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const hasFetched = useRef(false);

  // Fetch font list on first open
  useEffect(() => {
    if (!open || hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);
    fetchFontFamilies().then((f) => {
      setGoogleFamilies(f);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [open]);

  // Focus search input when opened
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); setSearch(''); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleSelect = useCallback(async (family: FontFamily) => {
    setLoadingFont(family.family_name);
    await loadFont(family.family_name, 400, false);
    setLoadingFont(null);
    onChange(family.family_name, family);
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const filteredBrand = search
    ? brandFamilies.filter((f) => f.family_name.toLowerCase().includes(search.toLowerCase()))
    : brandFamilies;
  const filteredGoogle = search
    ? googleFamilies.filter((f) => f.family_name.toLowerCase().includes(search.toLowerCase()))
    : googleFamilies;

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: 1 }}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 4,
          color: '#1e293b',
          padding: '3px 6px',
          fontSize: 12,
          textAlign: 'left',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value || 'Select font...'}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 2,
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          zIndex: 100,
          width: 220,
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Search input */}
          <div style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search fonts..."
              style={{
                width: '100%',
                border: '1px solid #e2e8f0',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: 12,
                outline: 'none',
                background: '#f8fafc',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Font list */}
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
                Loading fonts...
              </div>
            ) : filteredBrand.length === 0 && filteredGoogle.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
                No fonts found
              </div>
            ) : (
              <>
                {filteredBrand.length > 0 && (
                  <>
                    <div style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #f1f5f9' }}>Brand Fonts</div>
                    {filteredBrand.map((family) => (
                      <FontOption key={`b-${family.family_name}`} family={family} isSelected={family.family_name === value} isLoading={loadingFont === family.family_name} onSelect={() => handleSelect(family)} />
                    ))}
                  </>
                )}
                {filteredGoogle.length > 0 && (
                  <>
                    {filteredBrand.length > 0 && (
                      <div style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #f1f5f9' }}>Google Fonts</div>
                    )}
                    {filteredGoogle.map((family) => (
                      <FontOption key={`g-${family.family_name}`} family={family} isSelected={family.family_name === value} isLoading={loadingFont === family.family_name} onSelect={() => handleSelect(family)} />
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Individual font option with IntersectionObserver for lazy loading ───

function FontOption({
  family,
  isSelected,
  isLoading,
  onSelect,
}: {
  family: FontFamily;
  isSelected: boolean;
  isLoading: boolean;
  onSelect: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [fontLoaded, setFontLoaded] = useState(false);

  // Lazy-load font CSS when this option scrolls into view, cancel if it leaves
  useEffect(() => {
    const el = ref.current;
    if (!el || fontLoaded) return;
    let cancelled = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !cancelled) {
          loadFont(family.family_name, 400, false).then(() => {
            if (!cancelled) setFontLoaded(true);
          });
        }
      },
      { rootMargin: '50px' },
    );
    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [family.family_name, fontLoaded]);

  return (
    <div
      ref={ref}
      onClick={onSelect}
      style={{
        padding: '6px 10px',
        fontSize: 13,
        cursor: 'pointer',
        background: isSelected ? '#eff6ff' : isLoading ? '#f8fafc' : 'transparent',
        color: isLoading ? '#94a3b8' : '#1e293b',
        fontFamily: fontLoaded ? `"${family.family_name}", sans-serif` : 'inherit',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        borderBottom: '1px solid #fafafa',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.background = '#f8fafc';
      }}
      onMouseLeave={(e) => {
        if (!isSelected) e.currentTarget.style.background = 'transparent';
      }}
    >
      {isLoading ? 'Loading...' : family.family_name}
    </div>
  );
}
